import { Box, Button, Stack, Typography } from '@mui/material'
import { removeTeamMember } from 'api/teams'
import { useAuthContext } from 'components/context/AuthProvider'
import { useDrawersContext } from 'components/context/ClientsDrawersProvider'
import ConfirmModal from 'components/modals/ConfirmModal'
import NotificationSys from 'components/NotificationSystem'
import { UIUserIcon } from 'features/UI'
import { useShowControl } from 'hooks/useShowControl'
import React, { useCallback, useMemo } from 'react'
import { useMutation } from 'react-query'
import { TeamMember, allCaregiverRoles } from 'types'
import { useTeams } from '../../hooks/useTeams'
import { TimelineTeamMemberDrawer } from '../TimelineTeamMemberDrawer/TimelineTeamMemberDrawer'
import * as Styled from './TimelineTeamMemberItem.styled'

export function TimelineTeamMemberItem({ member }: { member: TeamMember }) {
  const { user, isTrainee } = useAuthContext()
  const { handleOpenProfile } = useDrawersContext()
  const { onRefetchTeamMembers, teamsData, activeTeamId, checkIsSystemManaged } = useTeams()

  const isLearningTeam = isTrainee && checkIsSystemManaged(activeTeamId)

  const isOwner = useMemo(() => {
    const activeTeam = teamsData.find((team) => team.id === activeTeamId)
    if (!activeTeam) return false
    return activeTeam.ownerId === member.userId || activeTeam.ownerId === member.user?.id
  }, [teamsData, activeTeamId, member])

  const isRegisteredMember = useMemo(() => {
    return !!member.user
  }, [member])

  const [isOpenMember, handleOpenMember, handleCloseMember] = useShowControl()

  const isCurrentUser = useMemo(() => {
    return user?.id === member.user?.id
  }, [user, member])

  const role = useMemo(() => {
    const roles = member.user?.role || member.role
    if (!roles || roles.length === 0) return ''

    return roles
      .map((role) => {
        const roleOption = allCaregiverRoles.find((option) => option.value === role)
        const label = roleOption?.label || role
        if (role === 'other') {
          return member.user?.otherRole || member.otherRole || label
        }
        return label
      })
      .join(', ')
  }, [member])

  const handleViewProfile = () => {
    if (isCurrentUser && member.user) {
      handleOpenProfile(member.user.id)
      return
    }

    handleOpenMember()
  }

  const [isOpenConfirmRemove, onOpenConfirmRemove, onCloseConfirmRemove] = useShowControl()

  const removeMemberMutation = useMutation(removeTeamMember, {
    onSuccess: () => {
      onRefetchTeamMembers()
      onCloseConfirmRemove()
      NotificationSys.showSuccess('User successfully removed from the team')
    },
  })

  const handleRemoveMember = useCallback(() => {
    removeMemberMutation.mutate({
      id: member.careTeamId,
      memberId: member.id,
    })
  }, [removeMemberMutation, member])

  return (
    <Styled.Container hasUser={isRegisteredMember}>
      <Stack direction="row" spacing={2} paddingBottom={1} alignItems="center">
        <Box display="flex" flexShrink={0}>
          <UIUserIcon
            firstName={member.user?.firstname || member.firstName}
            lastName={member.user?.lastname || member.lastName}
          />
        </Box>
        <Typography overflow="hidden" textOverflow="ellipsis">
          {member.user?.firstname || member.firstName} {member.user?.lastname || member.lastName}
          {role && `, ${role}`}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        {!isRegisteredMember && !isLearningTeam && (
          <Button size="small" variant="contained" color="error" onClick={onOpenConfirmRemove}>
            Remove
          </Button>
        )}
        <Button
          size="small"
          variant="contained"
          onClick={handleViewProfile}
          disabled={(isOwner && !isCurrentUser) || (isLearningTeam && !isCurrentUser)}
        >
          Edit
        </Button>
      </Stack>

      <TimelineTeamMemberDrawer
        isOpen={isOpenMember}
        onClose={handleCloseMember}
        teamMember={member}
      />

      {isOpenConfirmRemove && (
        <ConfirmModal
          title="Confirm User Removal"
          body="Are you sure you want to remove this user from the team?"
          handleClose={onCloseConfirmRemove}
          handleConfirm={handleRemoveMember}
          loading={removeMemberMutation.isLoading}
        />
      )}
    </Styled.Container>
  )
}
